import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Snackbar, { SnackbarOrigin } from "@mui/material/Snackbar";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";
import SuiPagination from "components/SuiPagination";
import Checkbox from "@mui/material/Checkbox";
import Pagination from "@mui/material/Pagination";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller } from "react-hook-form";

import { filterParser } from "utils/helper";
import { maCatOptions } from "utils/data";
import { MANUFACTURER_SERVICES } from "utils/data";
import { addProtocol, formatAddress } from "utils/helper";

import ManufacturerCard from "./components/ManufacturerCard";
import ManufacturerDetail from "pages/de/manufacturer-detail";
import PublishProjectBar from "pages/de/project/publish-project/PublishProjectBar";

import Prompt from "components/UpStyled/Prompt";
import qs from "query-string";

import curved8 from "assets/images/upstyled/default_background.png";
import { MAX_SELECTED_COUNT } from "utils/params";
import { matchYoutubeUrl } from "utils/imageHelper";
import { countries as countryData } from "utils/countries";

const ManufacturerList = ({
  handleOk = false,
  handleBack = false,
  handleCancel = false,
  handleOkText = "Ok",
  handleCancelText = "Cancel",
  handleBackText = "Back",
  selectable = false,
  project = {},
}) => {
  const [view, setView] = useState("grid");
  const [filteredUsers, setFilteredUsers] = useState({ hits: [], estimatedTotalHits: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchStr, setSearchStr] = useState("");
  const [filterObj, setFilterObj] = useState({});
  const [selectedUsers, setSelectedUsers] = useState({});
  const [user, setUser] = useState({});
  const [counterVisible, setCounterVisible] = useState(true);
  const [isPublic, setIsPublic] = useState(project?.visibility == "private" ? false : true);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const audiences = project?.audiences || [];

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();
  const location = useLocation();
  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);
  const userId = urlObj?.query?.userId;

  useEffect(() => {
    if (audiences && audiences.length > 0) {
      const initialSelectedUsers = {};
      for (let i = 0; i < audiences.length; i++) {
        initialSelectedUsers[audiences[i]] = true;
      }
      setSelectedUsers(initialSelectedUsers);
    }
  }, [audiences.toString(), userId]);

  useEffect(() => {
    if (userId) {
      retrieveUser(userId);
    } else {
      setUser({});
    }
  }, [userId, location]);

  useEffect(() => {
    if (!userId) {
      handleApply();
    } else {
      retrieveManufacturerList();
    }
  }, [url]);

  const handleApply = (input = {}) => {
    let data = getValues();
    data = { ...data, ...input };
    const offset = pageSize * (page - 1);
    const limit = pageSize;
    setSearchStr(data["keywords"]);
    let myFilter = {}; // { account_status: "active" } this will added at backend

    if (data["category"] && data["category"].value) {
      myFilter["store_manufacturing_specialties"] = { eq: data["category"].value };
    }
    if (data["moq"]) {
      let num = Number(data["moq"]);
      if (!isNaN(num)) {
        myFilter["store_moq"] = { gte: num };
      }
    }

    //service filter
    let service_offered = [];
    if (data["service_offered"]) {
      service_offered = data["service_offered"].map((x) => x.value);
      if (service_offered && service_offered.length > 0) {
        myFilter["store_services_offered"] = service_offered;
      }
    }

    if (data["country_code"]) {
      myFilter["store_country_code"] = data["country_code"].value;
    }

    if (data["province"]) {
      myFilter["store_province"] = data["province"].value;
    }

    retrieveManufacturerList(offset, limit, data["keywords"], myFilter);
  };

  const retrieveManufacturerList = async (offset = 0, limit = pageSize, q = "", filter = {}) => {
    // const { data } = await api.manufacturer.list({ offset, limit: pageSize });
    const payload = {
      offset,
      limit,
      sort: ["account_status:asc", "profile_update_level:desc"],
      facets: ["store_country_code", "store_province"],
    };
    if (q) {
      payload["q"] = q;
    }
    let myFilter = { account_type: "ma" };
    if (Object.keys(filter).length > 0) {
      myFilter = { ...myFilter, ...filter };
    }

    //filter out selected audiences
    if (audiences && audiences.length > 0) {
      myFilter["id"] = { neq: audiences };
    }

    const newFilterStr = filterParser(myFilter);
    if (newFilterStr) {
      payload["filter"] = newFilterStr;
      setFilterObj(myFilter);
    }
    const { data } = await api.users.search(payload);
    setFilteredUsers(data ? data : {});
  };

  const handleCheckUser = (e, id) => {
    const userIds = Object.keys(selectedUsers);
    let count = 0;
    for (let i = 0; i < userIds.length; i++) {
      if (selectedUsers[userIds[i]] == true) {
        count++;
      }
    }
    if (counterVisible == false) {
      setCounterVisible(true);
    }

    if (count >= MAX_SELECTED_COUNT && e?.target?.checked == true) {
      return toast.error(`You can only select up to ${MAX_SELECTED_COUNT} manufacturers!`);
    } else {
      let temp = {};
      temp[id] = typeof e == "boolean" ? e : e?.target?.checked;
      setSelectedUsers({ ...selectedUsers, ...temp });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const offset = pageSize * (newPage - 1);
    const limit = pageSize;
    retrieveManufacturerList(offset, limit, searchStr);
  };

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    const offset = 0;
    const limit = newPageSize;
    retrieveManufacturerList(offset, limit, searchStr);
  };

  const pageCount = parseInt(Math.ceil(filteredUsers.estimatedTotalHits / pageSize), 10);
  const pageOptions = [];
  for (let i = 0; i < pageCount; i++) {
    pageOptions.push(i);
  }
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd =
    pageSize * page > filteredUsers.estimatedTotalHits
      ? filteredUsers.estimatedTotalHits
      : pageSize * page;

  const retrieveUser = async (userId) => {
    if (!userId) {
      return;
    }
    try {
      const {
        data: { user },
      } = await api.users.retrieve(userId);
      if (user?.id) {
        setUser(user);
      } else {
        toast.error("User not existed!");
      }
    } catch (err) {
      console.log(err);
    }
  };
  const servicesOption = MANUFACTURER_SERVICES.map((x) => ({ label: x.label, value: x.value }));
  let selectedUserCount = 0;
  for (let u in selectedUsers) {
    if (selectedUsers[u] == true) {
      selectedUserCount++;
    }
  }

  const handleSendRequest = () => {
    if (selectedUserCount < 1) {
      setConfirmVisible(true);
    } else {
      const isPublic = project?.visibility ? project?.visibility : false;
      handleOk(selectedUsers, isPublic);
    }
  };

  const handlePublishToAll = () => {
    handleOk(selectedUsers, true);
  };

  const countryFacet = filteredUsers?.facetDistribution?.store_country_code || {};
  const provinceFacet = filteredUsers?.facetDistribution?.store_province || {};
  const allProvinces = Object.keys(provinceFacet);

  const countryOptions = countryData
    .filter((x) => countryFacet.hasOwnProperty(x?.alpha2.toLowerCase()))
    .map((x) => ({
      value: x?.alpha2.toLowerCase(),
      label: `${x?.name}(${countryFacet[x?.alpha2.toLowerCase()]})`,
    }));
  const provinceOptions = allProvinces.map((x) => ({
    value: x,
    label: `${x}(${provinceFacet[x]})`,
  }));

  return userId ? (
    <ManufacturerDetail user={user} />
  ) : (
    <div>
      {selectable && project?.id ? (
        <PublishProjectBar project={project} onPublish={handlePublishToAll} />
      ) : null}

      <SuiBox sx={{ p: 3 }}>
        <SuiTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {selectable ? "Send Requests" : "Manufacturer List"}
        </SuiTypography>
        {selectable ? (
          <Snackbar
            ContentProps={{
              classes: {
                root: "bg-white text-gray-700",
              },
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            open={counterVisible}
            //onClose={() => setCounterVisible(false)}
            message={`You already select ${selectedUserCount} out of ${MAX_SELECTED_COUNT} manufacturers.`}
            key={"counter_bar"}
            action={
              <React.Fragment>
                <IconButton
                  aria-label="close"
                  color="inherit"
                  sx={{ p: 0.5 }}
                  onClick={() => setCounterVisible(false)}
                >
                  <CloseIcon />
                </IconButton>
              </React.Fragment>
            }
          />
        ) : null}
        {selectable ? (
          <SuiBox sx={{ mb: 1 }}>
            <SuiTypography
              variant="button"
              fontWeight="regular"
              color="text"
              sx={{ lineHeight: 1.2, mb: 1 }}
            >
              You may select up to{" "}
              <SuiBox style={{ fontWeight: "bold" }} color="dark" component="span">
                {MAX_SELECTED_COUNT}
              </SuiBox>{" "}
              manufacturers to share your project with. After you click "Publish to all" button at
              the top of this page, selected manufacturers will be able to review your requests and
              get back to you.
            </SuiTypography>
            {audiences && audiences.length > 0 ? (
              <SuiTypography variant="button" fontWeight="regular" color="text">
                You already sent the project request to{" "}
                <SuiBox style={{ fontWeight: "bold" }} color="dark" component="span">
                  {audiences.length}
                </SuiBox>{" "}
                manufacturers.
              </SuiTypography>
            ) : null}
          </SuiBox>
        ) : null}
        <SuiBox display="flex" justifyContent="space-between" alignItems="flex-end">
          <SuiBox component="form" pb={1} onSubmit={handleSubmit(handleApply)}>
            <SuiBox display="flex" alignItems="center" sx={{ gap: 1, flexWrap: "wrap" }}>
              <SuiTypography variant="button" color="dark" fontWeight="regular">
                Filter by:
              </SuiTypography>
              <div>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Card sx={{ borderRadius: 2 }}>
                      <SuiSelect
                        {...field}
                        options={maCatOptions}
                        placeholder="Category"
                        isClearable
                        onChange={(newValue, actionMeta) => {
                          let payload = {};
                          payload[field.name] = newValue;
                          handleApply(payload);
                          field.onChange(newValue);
                        }}
                      />
                    </Card>
                  )}
                />
              </div>
              <Controller
                name="service_offered"
                control={control}
                render={({ field }) => (
                  <Card sx={{ borderRadius: 2 }}>
                    <SuiSelect
                      {...field}
                      options={servicesOption}
                      placeholder="Service Offered"
                      isMulti
                      closeMenuOnSelect={false}
                      backspaceRemovesValue
                      isClearable
                      onChange={(newValue, actionMeta) => {
                        let payload = {};
                        payload[field.name] = newValue;
                        handleApply(payload);
                        field.onChange(newValue);
                      }}
                    />{" "}
                  </Card>
                )}
              />
              <div>
                <SuiInput placeholder="MOQ" type="number" min={0} {...register("moq")} />
              </div>
              <Controller
                name="country_code"
                control={control}
                render={({ field }) => (
                  <Card sx={{ borderRadius: 2 }}>
                    <SuiSelect
                      {...field}
                      options={countryOptions}
                      placeholder="Country"
                      backspaceRemovesValue
                      isClearable
                      onChange={(newValue, actionMeta) => {
                        let payload = {};
                        payload[field.name] = newValue;
                        handleApply(payload);
                        field.onChange(newValue);
                      }}
                    />{" "}
                  </Card>
                )}
              />
              <Controller
                name="province"
                control={control}
                render={({ field }) => (
                  <Card sx={{ borderRadius: 2 }}>
                    <SuiSelect
                      {...field}
                      options={provinceOptions}
                      placeholder="Province/State"
                      backspaceRemovesValue
                      isClearable
                      onChange={(newValue, actionMeta) => {
                        let payload = {};
                        payload[field.name] = newValue;
                        handleApply(payload);
                        field.onChange(newValue);
                      }}
                    />{" "}
                  </Card>
                )}
              />
              <div>
                <SuiInput placeholder="Keywords" type="text" {...register("keywords")} />
              </div>
              <div>
                <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
                  Apply
                </SuiButton>
              </div>
            </SuiBox>
          </SuiBox>
        </SuiBox>
        <SuiBox pb={3}></SuiBox>
        <div>
          <Grid container spacing={3}>
            {filteredUsers.hits.map((u) => {
              const store = u?.stores && u.stores.length > 0 ? u.stores[0] : {};
              const serviceStages = store?.metadata?.services_offered
                ? Object.keys(store.metadata.services_offered)
                : [];
              let image = "";
              const gallery = store?.gallery || [];
              for (let i = 0; i < gallery.length; i++) {
                //find first available image on  gallery

                if (
                  gallery[i].startsWith("blob") ||
                  matchYoutubeUrl(gallery[i]) ||
                  gallery[i].endsWith(".mp4")
                ) {
                } else {
                  image = gallery[i];
                  break;
                }
              }

              if (!image) {
                //trying to use background image
                image = store?.metadata?.background_image;
              }

              if (!image) {
                //using default image
                image = curved8;
              }

              return (
                <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                  <ManufacturerCard
                    selectable={selectable}
                    isSelected={selectedUsers[u.id] ? true : false}
                    handleSelectUser={(e) => handleCheckUser(e, u.id)}
                    image={image}
                    title={store?.name}
                    description={store?.detail}
                    story={store?.story}
                    address={formatAddress(store?.metadata?.address, true)}
                    rating={store?.rating}
                    services={serviceStages}
                    account_status={u?.account_status}
                    action={{
                      route: `/wall/portfolio/${u.id}`,
                      color: "info",
                      label: "view profile",
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </div>

        <SuiBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={3}
        >
          <div className="flex items-center">
            <SuiBox mb={{ xs: 3, sm: 0 }}>
              <SuiTypography variant="button" color="secondary" fontWeight="regular">
                Showing {entriesStart} to {entriesEnd} of {filteredUsers.estimatedTotalHits} entries
              </SuiTypography>
            </SuiBox>

            <SuiBox display="flex" alignItems="center" gap={1}>
              <SuiSelect
                defaultValue={{ value: pageSize, label: pageSize }}
                options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
                onChange={handleChangeRowsPerPage}
                size="small"
              />
              <SuiTypography variant="caption" color="secondary">
                entries per page
              </SuiTypography>
            </SuiBox>
          </div>
          <Pagination
            variant={"outlined"}
            color={"info"}
            count={pageOptions.length}
            defaultPage={page}
            onChange={handleChangePage}
          />
        </SuiBox>
        {selectable ? (
          <SuiBox>
            {/* <SuiBox>
              <SuiTypography
                variant="body2"
                fontWeight="regular"
                color="text"
                sx={{ lineHeight: 1.2, mb: 1 }}
              >
                <SuiBox style={{ fontWeight: "bold" }} color="dark" component="span">
                  Publish to All:
                </SuiBox>{" "}
                To maximize the visibility of your project, you may select “Publish to All” to post
                this listing on our public job board for manufacturers.
              </SuiTypography>
            </SuiBox> */}

            <SuiBox>
              <SuiTypography
                variant="body2"
                fontWeight="regular"
                color="text"
                sx={{ lineHeight: 1.2, mb: 1 }}
              >
                <SuiBox style={{ fontWeight: "bold" }} color="dark" component="span">
                  Send Request:
                </SuiBox>{" "}
                Once you send your requests to manufacturers, manufacturers you selected can see
                your request and respond to you. You will be able to see and edit your project under
                the “Requested” project list. You can also send more requests to other manufacturers
                by visiting your project page.
              </SuiTypography>
            </SuiBox>
          </SuiBox>
        ) : null}
        {handleOk ? (
          <SuiBox
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="flex-end"
          >
            <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
              <SuiButton
                variant="gradient"
                color={"dark"}
                sx={{ height: "100%" }}
                onClick={handleSendRequest}
              >
                {handleOkText}
              </SuiButton>
            </SuiBox>
          </SuiBox>
        ) : null}
      </SuiBox>
      {confirmVisible ? (
        <Prompt
          heading="Are you sure to not select any manufacturers?"
          text={`You may need to select at least one manufacturer when project is private, otherwise no manufacturers can see your project.`}
          visible={confirmVisible}
          okText="Confirm"
          okColor="dark"
          handleOk={() => {
            handleOk(selectedUsers, isPublic);
            setConfirmVisible(false);
          }}
          handleClose={() => setConfirmVisible(false)}
        />
      ) : null}
    </div>
  );
};

export default ManufacturerList;
